import { Button, CircularProgress, Stack, Typography } from '@mui/material'
import { Feature, OnboardingStep, TrendTopic, TwinInsightCategory, TwinInsightsWithLatest } from 'api/model'
import { useCallback, useEffect, useMemo, useState } from 'react'
import SetupPermissionsModal from 'components/Permissions/SetupPermissionsModal'
import { useFetchCheckPermissions } from 'hooks/use-permission'
import { ReactComponent as GmailSlackInsights } from './gmail-slack-insights.svg'
import { ReactComponent as GoogleIcon } from 'components/Login/provider-icons/google.svg'
import { ReactComponent as SlackIcon } from 'components/NavBar/slack.svg'
import InsightsList from './InsightsList'
import styled from '@emotion/styled'
import { useActiveOrganization } from 'hooks/use-organization'
import { API, COLORS } from 'app-constants'
import { authenticatedUserAtom, toastAtom } from 'state'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { AddSlackToInsightsBanner } from 'components/Slack'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import AddTopicModal from './AddTopicModal'
import { myTwinAtom } from 'state/my-twin'
import { insightCategoriesAtom, insightsCategoriesLoadingAtom } from 'state/insights'
import { trendsBuild, usersUpdateTwinResponder } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import withOnboardingStep, { UpdateOnboardingStepOption } from 'components/FTUE/withOnboardingStep'
import useFlag from 'hooks/use-flag'
import { TopicLabelModal } from './TopicLabel/TopicLabel'
import { useSearchParams } from 'react-router-dom'
import { useFetchTwinResponder } from 'hooks/use-email-responder'
import UserInfoModal from 'components/FTUE/UserInfoModal'

const GrantPermissionsContainerStack = styled(Stack)`
  padding: 40px;
  justify-content: center;
  align-items: center;
  max-height: 559px;
  width: 100%;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: '2px 2px 20px 0px rgba(0, 0, 0, 0.05)';
`

export const GrantPermissions = ({
  icon,
  title,
  descriptions,
  buttons,
}: {
  icon: React.ReactNode
  title: string
  descriptions: string[]
  buttons: React.ReactNode
}) => {
  return (
    <GrantPermissionsContainerStack>
      {icon}
      <Typography variant="h5" mb={3} mt={5}>
        {title}
      </Typography>

      <Stack spacing={1}>
        {descriptions.map((d) => (
          <Typography
            textAlign="center"
            key={d}
            variant="pS"
            sx={{
              color: COLORS.GRAY_2,
            }}>
            {d}
          </Typography>
        ))}
      </Stack>
      {buttons}
    </GrantPermissionsContainerStack>
  )
}

const Insights = ({
  isLoadingTwinListInsights,
  twinInsightsWithLatest,
  selectedCategory,
  selectedTrend,
  refetchTwinInsightsWithLatest,
  updateOnboardingStep,
}: {
  isLoadingTwinListInsights: boolean
  twinInsightsWithLatest: TwinInsightsWithLatest | null
  selectedCategory: TwinInsightCategory | null
  selectedTrend: TrendTopic | null
  refetchTwinInsightsWithLatest: () => void
  updateOnboardingStep: (steps?: UpdateOnboardingStepOption) => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const [user, setUser] = useRecoilState(authenticatedUserAtom)
  const isMobile = useCheckMobileScreen()
  const myTwin = useRecoilValue(myTwinAtom)
  const setToast = useSetRecoilState(toastAtom)

  const [insightCategories, setInsightCategories] = useRecoilState(insightCategoriesAtom)
  const [isLoadingInsightCategories] = useRecoilState(insightsCategoriesLoadingAtom)

  const { featurePermissions: gmailFeaturePermissions, isLoading: isLoadingGmailPermissions } =
    useFetchCheckPermissions(Feature.insights)

  const { featurePermissions: advancedSettingsFeaturePermissions } = useFetchCheckPermissions(
    Feature.advanced_email_responder
  )

  const hasGmailPermissions = gmailFeaturePermissions?.isPermitted ?? false
  const acceptedPermissions = gmailFeaturePermissions?.acceptedPermissions ?? []
  const missingPermissions = useMemo(() => {
    return gmailFeaturePermissions?.missingPermissions ?? []
  }, [gmailFeaturePermissions?.missingPermissions])

  // trendsBuild API may be disabled if we are in a dev environment
  const flags = useFlag('disable-trends-build')

  const hasSlackPermissions = user?.has_slack

  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState<boolean>(false)
  const onClosePermissionsModal = () => {
    setIsPermissionsModalOpen(false)
  }

  const [isAddTopicModalOpen, setIsAddTopicModalOpen] = useState(false)
  const onAddTopicClick = () => {
    setIsAddTopicModalOpen(true)
  }

  const [isTopicLabelModalOpen, setIsTopicLabelModalOpen] = useState(false)

  // this is shown for existing users that have not yet set their role / function
  const [isUserInfoModalOpen, setIsUserInfoModalOpen] = useState(!user?.role || !user?.function ? true : false)
  const onCloseUserInfoModal = () => {
    setIsUserInfoModalOpen(false)
  }

  // we need to update the responder status in the UI if the user enables the use of insight topics as labels
  const { twinResponder, isLoading: isLoadingTwinResponder } = useFetchTwinResponder(myTwin?.id ?? '')

  const onEnableUseInsightTopicsAsLabels = useCallback(async () => {
    if (!activeOrganization || !myTwin?.id) {
      return
    }
    try {
      await usersUpdateTwinResponder(activeOrganization, myTwin.id, {
        use_insight_topics_as_labels: true,
      })

      updateOnboardingStep({
        onboardingSteps: [OnboardingStep.topic_label_modal_shown],
      })
      setUser((prev) => {
        if (!prev) {
          return prev
        }
        return {
          ...prev,
          onboarding_steps_completed: [...prev.onboarding_steps_completed, OnboardingStep.topic_label_modal_shown],
        }
      })

      setToast({
        title: 'Success! Your topics are now being applied as labels in Gmail',
        severity: 'success',
        description: 'You can change this in Settings < Emails',
      })

      setIsTopicLabelModalOpen(false)
    } catch (error) {
      setToast({
        title: 'Failed to set topics as labels in Gmail',
        severity: 'error',
      })
    }
  }, [activeOrganization, myTwin?.id, setToast, updateOnboardingStep, setUser])

  const [searchParams] = useSearchParams()
  // automatically enable the use of insight topics as labels in Gmail if the search param is enabled
  useEffect(() => {
    async function updateTwinResponder() {
      if (!activeOrganization || !myTwin?.id) {
        return
      }

      try {
        onEnableUseInsightTopicsAsLabels()
        // clear out the search param so that this call only happens once
        window.history.replaceState({}, '', window.location.pathname)
      } catch {
        setToast({
          title: 'Failed to set topics as labels in Gmail',
          severity: 'error',
        })
      }
    }

    // after granting advanced permissions, the url will contain 'isEnabledUseInsightTopicsAsLabels=true'
    // we then want to automatically update the property in twinResponder to use insight topics as labels
    if (
      !user?.onboarding_steps_completed.includes(OnboardingStep.topic_label_modal_shown) &&
      searchParams.get('isEnabledUseInsightTopicsAsLabels') === 'true'
    ) {
      updateTwinResponder()
    }
  }, [
    twinResponder,
    onEnableUseInsightTopicsAsLabels,
    searchParams,
    activeOrganization,
    myTwin?.id,
    setToast,
    updateOnboardingStep,
    user?.onboarding_steps_completed,
    setUser,
  ])

  useEffect(() => {
    // if the topic label modal has not been shown yet, we should show it
    if (
      !user?.onboarding_steps_completed.includes(OnboardingStep.topic_label_modal_shown) &&
      user?.objectives.length === 0
    ) {
      setIsTopicLabelModalOpen(true)
    } else {
      setIsTopicLabelModalOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const triggerTrendsBuild = useCallback(async () => {
    if (!activeOrganization || !myTwin?.id) {
      return
    }

    try {
      const isTrendsBuildDisabled = flags.feature.active
      if (isTrendsBuildDisabled) {
        return
      }

      if (missingPermissions.length === 0) {
        await trendsBuild(activeOrganization, myTwin.id)
        updateOnboardingStep({
          onboardingSteps: [OnboardingStep.insights_generation_triggered],
        })
        setUser((prev) => {
          if (!prev) {
            return prev
          }
          return {
            ...prev,
            onboarding_steps_completed: [
              ...prev.onboarding_steps_completed,
              OnboardingStep.insights_generation_triggered,
            ],
          }
        })
      }
    } catch (error) {
      setToast({
        title: 'Failed to generate insights',
        severity: 'error',
      })
    }
  }, [
    activeOrganization,
    flags.feature.active,
    missingPermissions.length,
    myTwin?.id,
    setToast,
    updateOnboardingStep,
    setUser,
  ])

  // when the insights page is loaded and the user hasn't generated the trends yet, we should trigger it
  useEffect(() => {
    if (!activeOrganization) {
      return
    }

    if (!user?.onboarding_steps_completed.includes(OnboardingStep.insights_generation_triggered)) {
      if (myTwin?.id && !flags.feature.active) {
        triggerTrendsBuild()
      }
    }
    // on initial load, we only want to trigger the trends build once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onCloseTopicLabelModal = () => {
    setIsTopicLabelModalOpen(false)

    // if the user closes the modal and has granted advanced permissions, we should set use_insight_topics_as_labels to true
    if (advancedSettingsFeaturePermissions?.missingPermissions.length === 0) {
      onEnableUseInsightTopicsAsLabels()
    } else {
      // if user tried to close the modal but did not grant permissions, we should still update onboarding_steps_completed
      updateOnboardingStep({
        onboardingSteps: [OnboardingStep.topic_label_modal_shown],
      })
    }
  }

  if (isLoadingGmailPermissions || isLoadingTwinResponder) {
    return (
      <Stack height="100%" width={isMobile ? '100%' : '960px'} justifyContent="center" alignItems="center">
        <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
      </Stack>
    )
  }

  if (!hasGmailPermissions) {
    return (
      <Stack>
        <GrantPermissions
          icon={<GmailSlackInsights />}
          title="Get key insights from Gmail and Slack"
          descriptions={[
            'Efficiently keep tabs on all incoming emails and messages through insights.',
            'Customize your topics of interest to create a personalized feed.',
            'To enable this feature, please grant permissions for Google and Slack (optional).',
          ]}
          buttons={
            <Stack direction="row" alignItems="center" spacing={2} mt={5}>
              {!hasGmailPermissions && (
                <Button
                  startIcon={<GoogleIcon />}
                  onClick={() => {
                    setIsPermissionsModalOpen(true)
                  }}
                  variant="outlined"
                  sx={{
                    color: COLORS.GRAY_0,
                    background: COLORS.PURE_WHITE,
                    border: `1px solid ${COLORS.GRAY_5}`,
                  }}>
                  Google
                </Button>
              )}
              {!hasSlackPermissions && (
                <Button
                  startIcon={<SlackIcon />}
                  onClick={() => {
                    const url = `${API.ORIGIN}/api/auth/oauth/slack-install?org_id=${activeOrganization}`
                    window.open(url)
                  }}
                  variant="outlined"
                  sx={{
                    color: COLORS.GRAY_0,
                    background: COLORS.PURE_WHITE,
                    border: `1px solid ${COLORS.GRAY_5}`,
                  }}>
                  Slack
                </Button>
              )}
            </Stack>
          }
        />

        {isPermissionsModalOpen && (
          <SetupPermissionsModal
            onClose={onClosePermissionsModal}
            acceptedPermissions={acceptedPermissions}
            missingPermissions={missingPermissions}
            feature={Feature.insights}
          />
        )}
      </Stack>
    )
  }

  // we only show the add your own topics experience if there are no topics that the user selected
  if (insightCategories.length === 0 && !isLoadingInsightCategories && myTwin?.insights_onboarding_percent === 100) {
    return (
      <Stack spacing={2}>
        {!hasSlackPermissions && <AddSlackToInsightsBanner user={user} />}
        <Stack
          p="80px 16px"
          alignItems="center"
          sx={{
            background: COLORS.PURE_WHITE,
          }}>
          <Typography
            variant="h3"
            mb="20px"
            sx={{
              color: COLORS.GRAY_4,
            }}>
            No insights to show yet
          </Typography>

          <Typography
            variant="pS"
            sx={{
              my: 5,
              color: COLORS.GRAY_2,
            }}>
            Add your own topics or suggested topics to see insights from your email.
          </Typography>

          <Button
            variant="contained"
            color="primary"
            onClick={onAddTopicClick}
            sx={{
              width: 'fit-content',
            }}>
            Add topic
          </Button>
        </Stack>
        {isAddTopicModalOpen && (
          <AddTopicModal
            twinId={myTwin?.id ?? ''}
            insightCategories={insightCategories}
            onUpdateInsights={(updatedInsights: TwinInsightCategory[]) => {
              // update local state with new list of insights
              setInsightCategories(updatedInsights)

              // refetch twin insights with latest as we have new topics in our sidebar
              refetchTwinInsightsWithLatest()
            }}
            onClose={() => setIsAddTopicModalOpen(false)}
          />
        )}
      </Stack>
    )
  }

  return (
    <Stack spacing={2} width={isMobile ? '100%' : '960px'} maxWidth={isMobile ? '100%' : '960px'}>
      {!hasSlackPermissions && <AddSlackToInsightsBanner user={user} />}
      <Stack direction={isMobile ? 'column' : 'row'} alignItems="flex-start" spacing={isMobile ? 2 : 5} width="100%">
        <InsightsList
          latest={twinInsightsWithLatest?.latest ?? []}
          insights={twinInsightsWithLatest?.insights ?? []}
          selectedCategory={selectedCategory}
          selectedTrend={selectedTrend}
          isLoadingTwinListInsights={isLoadingTwinListInsights}
        />
      </Stack>

      {isTopicLabelModalOpen && (
        <TopicLabelModal
          advancedSettingsFeaturePermissions={advancedSettingsFeaturePermissions}
          onClose={onCloseTopicLabelModal}
        />
      )}
      {isUserInfoModalOpen && <UserInfoModal onClose={onCloseUserInfoModal} />}
    </Stack>
  )
}

export default withOnboardingStep(Insights, OnboardingStep.insights_generation_triggered)
